import React, { useEffect } from "react";
import Swal from "sweetalert2";
import PageTitle from "../store/PageTitle";
import DOMPurify from "dompurify";
import { useSelector, useDispatch } from "react-redux";
import { fetchAbout } from "../store/action/contentAction";

import "./about.css";

function About({ html }) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAbout());
  }, [dispatch]);

  const aboutData = useSelector((state) => state.about.data) || [];

  const handleSoon = () => {
    Swal.fire({
      icon: "warning",
      title: "Coming Soon",
      text: "Newsletter will be available soon",
      confirmButtonText: "Okay",
    });
  };

  const styles = {
    containerNew: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "60px 20px",
      fontFamily: "Arial, sans-serif",
      lineHeight: "1.6",
      color: "#333",
      textAlign: "left",
    },
    header: {
      textAlign: "center",
      marginBottom: "50px",
    },
    title: {
      fontSize: "2.5rem",
      fontWeight: "bold",
      color: "#2c3e50",
      marginBottom: "15px",
    },
    subtitle: {
      fontSize: "1.1rem",
      color: "#7f8c8d",
      maxWidth: "600px",
      margin: "0 auto",
    },
    content: {
      gridTemplateColumns: "1fr 1fr",
      gap: "40px",
      alignItems: "start",
      marginBottom: "50px",
    },
    textSection: {
      display: "flex",
      flexDirection: "column",
      gap: "30px",
    },
    sectionView: {
      backgroundColor: "#f8f9fa",
      padding: "25px",
      borderRadius: "10px",
      borderLeft: "4px solid #3498db",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
    },
    sectionHover: {
      transform: "translateY(-5px)",
      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
    },
    sectionTitle: {
      fontSize: "1.4rem",
      fontWeight: "bold",
      color: "#2c3e50",
      marginBottom: "12px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    sectionText: {
      fontSize: "1rem",
      color: "#555",
      lineHeight: "1.7",
    },
    imageSection: {
      position: "relative",
      height: "500px",
      borderRadius: "15px",
      overflow: "hidden",
      boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    },
    teamImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: "transform 0.3s ease",
    },
    imageOverlay: {
      position: "absolute",
      bottom: "0",
      left: "0",
      right: "0",
      background: "linear-gradient(transparent, rgba(7, 5, 5, 0.9))",
      color: "white",
      padding: "30px 25px 25px",
      transform: "translateY(0)",
      transition: "transform 0.3s ease",
    },
    overlayTitle: {
      fontSize: "1.5rem",
      fontWeight: "bold",
      marginBottom: "8px",
    },
    overlayText: {
      fontSize: "0.95rem",
      opacity: "0.9",
    },
    responsive: {
      "@media (max-width: 768px)": {
        gridTemplateColumns: "1fr",
        gap: "30px",
      },
    },
  };

  const [hoveredSection, setHoveredSection] = React.useState(null);

  return (
    <div>
      <PageTitle title="About Us" />
      <section id="page-header" className="about-header">
        <h2>#knowUs</h2>
        <p>For all your electronics we got you covered</p>
      </section>
      {aboutData.map((item, idx) => (
        <div style={styles.containerNew} key={idx}>
          <div style={styles.header}>
            <div
              style={styles.subtitle}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(item.title),
              }}
            />
          </div>

          <div
            style={{
              ...styles.content,
              ...(window.innerWidth <= 768 && {
                gridTemplateColumns: "1fr",
                gap: "30px",
              }),
            }}
          >
            <div style={styles.textSection}>
              <div
                style={{
                  ...styles.sectionView,
                  ...(hoveredSection === "description" && styles.sectionHover),
                }}
                onMouseEnter={() => setHoveredSection("description")}
                onMouseLeave={() => setHoveredSection(null)}
              >
                <h3 style={styles.sectionTitle}>
                  <span style={{ fontSize: "1.2rem" }}>📖</span>
                  Our Story
                </h3>

                <div
                  style={styles.sectionText}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(item.description),
                  }}
                />
              </div>
              <div
                style={{
                  ...styles.sectionView,
                  borderLeftColor: "#27ae60",
                  ...(hoveredSection === "vision" && styles.sectionHover),
                }}
                onMouseEnter={() => setHoveredSection("vision")}
                onMouseLeave={() => setHoveredSection(null)}
              >
                <h3 style={styles.sectionTitle}>
                  <span style={{ fontSize: "1.2rem" }}>👁️</span>
                  Our Vision
                </h3>

                <div
                  style={styles.sectionText}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(item.vision),
                  }}
                />
              </div>
              <div
                style={{
                  ...styles.sectionView,
                  borderLeftColor: "#e74c3c",
                  ...(hoveredSection === "mission" && styles.sectionHover),
                }}
                onMouseEnter={() => setHoveredSection("mission")}
                onMouseLeave={() => setHoveredSection(null)}
              >
                <h3 style={styles.sectionTitle}>
                  <span style={{ fontSize: "1.2rem" }}>🎯</span>
                  Our Mission
                </h3>

                <div
                  style={styles.sectionText}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(item.mission),
                  }}
                />
              </div>

              <div
                style={{
                  ...styles.sectionView,
                  borderLeftColor: "rgba(96, 96, 100, 1)",
                  ...(hoveredSection === "coreValues" && styles.sectionHover),
                }}
                onMouseEnter={() => setHoveredSection("coreValues")}
                onMouseLeave={() => setHoveredSection(null)}
              >
                <h3 style={styles.sectionTitle}>
                  <span style={{ fontSize: "1.2rem" }}>💎</span>
                  Core Values
                </h3>
                <div
                  style={styles.sectionText}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(item.coreValues),
                  }}
                />
              </div>
            </div>

            <div style={styles.imageSection}>
              <img src={item.teamImg} alt="Our Team" style={styles.teamImage} />
              <div style={styles.imageOverlay}>
                <h4 style={styles.overlayTitle}>Meet Our Team</h4>

                <div
                  style={styles.overlayText}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(item.teamText),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
      <section id="newsletter" className="section-pl section-ml">
        <div className="newstext">
          <h4>Sign Up For Newsletter</h4>
          <p>
            Get E-mail updates about our latest shop and{" "}
            <span> special offers</span>
          </p>
        </div>
        <div className="form">
          <input type="text" placeholder="Your email address" disabled />
          <button className="normal" onClick={handleSoon}>
            Sign Up
          </button>
        </div>
      </section>
    </div>
  );
}
export default About;
